import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = { name: "", parent_id: "", order: 0, active: true, image: "", show_in_menu: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const roots = categories.filter((c) => !c.parent_id);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, parent_id: c.parent_id || "", order: c.order || 0, active: c.active,
      image: c.image || "", show_in_menu: c.show_in_menu !== false,
    });
    setDialogOpen(true);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatApiError(err, "Falha no upload da imagem."));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Informe o nome da categoria.");
    setSaving(true);
    const payload = { ...form, parent_id: form.parent_id || null, order: Number(form.order) || 0 };
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, payload);
        toast.success("Categoria atualizada!");
      } else {
        await api.post("/admin/categories", payload);
        toast.success("Categoria criada!");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/categories/${deleting.id}`);
      toast.success("Categoria excluída.");
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-bold text-gray-900">Categorias</h1>
          <p className="text-sm text-gray-500 mt-1">Organize o catálogo em categorias e subcategorias</p>
        </div>
        <Button onClick={openCreate} className="rounded-full" data-testid="category-create-button">
          <Plus className="w-4 h-4" /> Nova Categoria
        </Button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="p-4">Nome</th>
              <th className="p-4">Tipo</th>
              <th className="p-4">Produtos</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">Nenhuma categoria cadastrada.</td></tr>
            ) : (
              categories.map((c) => (
                <tr key={c.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`admin-category-row-${c.id}`}>
                  <td className="p-4 font-medium text-gray-800">
                    {c.parent_id && <span className="text-slate-300 mr-2">↳</span>}{c.name}
                  </td>
                  <td className="p-4 text-slate-600">{c.parent_id ? "Subcategoria" : "Categoria"}</td>
                  <td className="p-4 text-slate-600">{c.product_count ?? 0}</td>
                  <td className="p-4">
                    <div className="flex flex-wrap gap-1.5">
                      <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${c.active ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                        {c.active ? "Ativa" : "Inativa"}
                      </span>
                      {c.show_in_menu === false && (
                        <span className="text-xs font-semibold rounded-full px-2.5 py-1 bg-slate-100 text-slate-500" data-testid={`category-hidden-menu-${c.id}`}>
                          fora do menu
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`category-edit-${c.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => setDeleting(c)} data-testid={`category-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="category-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Categoria" : "Nova Categoria"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" required data-testid="category-form-name" />
            </div>
            <div>
              <Label>Categoria pai (para subcategorias)</Label>
              <Select value={form.parent_id || "none"} onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="category-form-parent">
                  <SelectValue placeholder="Nenhuma" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma — é uma categoria principal</SelectItem>
                  {roots.filter((r) => r.id !== editing?.id).map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ordem no menu</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="mt-1.5" data-testid="category-form-order" />
            </div>
            <div>
              <Label>Imagem da categoria (vitrine da página inicial)</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {form.image ? (
                  <img src={form.image} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
                ) : (
                  <div className="w-16 h-16 rounded-lg bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center text-[9px] text-slate-400 text-center">
                    sem imagem
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Input
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    placeholder="https://... (URL da imagem)"
                    className="h-9 text-xs"
                    data-testid="category-form-image"
                  />
                  <label className="inline-flex items-center gap-1.5 text-xs text-brand font-medium cursor-pointer hover:underline">
                    <Upload className="w-3.5 h-3.5" /> Enviar do computador
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="category-form-upload" />
                  </label>
                </div>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="category-form-active" />
              Ativa na loja
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <Switch checked={form.show_in_menu} onCheckedChange={(v) => setForm({ ...form, show_in_menu: v })} data-testid="category-form-show-in-menu" />
              Aparecer no menu de navegação
            </label>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="category-form-save">
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir categoria?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.name}" será removida. Categorias com produtos não podem ser excluídas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="category-delete-confirm">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
